import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { tForLang } from "@/lib/i18n";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { motion } from "framer-motion";
import { ShieldCheck, CheckCircle2, Upload, X, FileText, Globe, Check } from "lucide-react";

const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
});

export default function PublicWithdrawalForm() {
    const { slug } = useParams();
    const [shop, setShop] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);
    const [lang, setLang] = useState("de");
    const [files, setFiles] = useState([]);
    const [form, setForm] = useState({
        full_name: "", email: "", address_line1: "", address_line2: "",
        postal_code: "", city: "", country: "Deutschland",
        order_number: "", order_date: "", products: "", reason: "", iban: "",
    });

    useEffect(() => {
        api.get(`/public/shops/${slug}`)
            .then(({ data }) => {
                setShop(data);
                setLang(data.language || "de");
                if (data.country) setForm((f) => ({ ...f, country: data.country }));
            })
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [slug]);

    const tt = (k) => tForLang(lang, k);
    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onFiles = async (fl) => {
        const arr = Array.from(fl || []);
        const allowed = arr.filter((f) => f.size <= 5 * 1024 * 1024); // 5MB each
        if (allowed.length !== arr.length) {
            toast.error(tt("toast_file_too_large"));
        }
        const limit = Math.min(5, allowed.length);
        const next = [];
        for (let i = 0; i < limit; i++) {
            const f = allowed[i];
            const data = await fileToBase64(f);
            next.push({ filename: f.name, content_type: f.type || "application/octet-stream", data });
        }
        setFiles((existing) => [...existing, ...next].slice(0, 5));
    };

    const removeFile = (i) => setFiles((arr) => arr.filter((_, idx) => idx !== i));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = { ...form, attachments: files, language: lang };
            const { data } = await api.post(`/public/withdrawals/${slug}`, payload);
            setSuccess(data);
        } catch (err) {
            const detail = err?.response?.data?.detail || "";
            if (detail === "order_not_found") {
                toast.error(tt("error_order_not_found"));
            } else if (detail === "email_mismatch") {
                toast.error(tt("error_email_mismatch"));
            } else if (detail === "b2b_order") {
                toast.error(tt("error_b2b_order"));
            } else if (detail.startsWith("withdrawal_period_expired:")) {
                const days = detail.split(":")[1];
                toast.error(tt("error_withdrawal_period_expired").replace("{days}", days));
            } else if (detail === "withdrawal_limit_reached") {
                toast.error(tt("error_withdrawal_limit_reached"));
            } else {
                toast.error(detail || tt("toast_submit_failed"));
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">{tt("loading")}</div>;
    }
    if (notFound) {
        return (
            <div className="min-h-screen flex items-center justify-center px-6 text-center">
                <div>
                    <h1 className="font-display text-3xl font-bold text-[#00153f]">{tt("public_not_found_title")}</h1>
                    <p className="text-gray-500 mt-2">{tt("public_not_found_sub")}</p>
                </div>
            </div>
        );
    }

    const brand = shop?.brand_color || "#0b905d";
    const headerTextColor = shop?.header_text_dark ? "#000000" : "#ffffff";

    if (success) {
        return (
            <div className="min-h-screen bg-slate-50">
                <header style={{ background: brand, color: headerTextColor }}>
                    <div className="max-w-3xl mx-auto px-6 py-8 flex items-center gap-3">
                        {shop?.logo_url ? <img src={shop.logo_url} alt="" className="h-9 rounded-md p-1" style={{ background: `${headerTextColor}1a` }} /> : null}
                        <div className="font-display font-bold">{shop?.name}</div>
                    </div>
                </header>
                <div className="max-w-2xl mx-auto px-6 py-16">
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-gray-200 rounded-md p-10 text-center" data-testid="public-success">
                        <div className="h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto">
                            <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                        </div>
                        <h1 className="font-display text-3xl font-bold text-[#00153f] mt-6">{tt("public_success_title")}</h1>
                        <p className="text-gray-600 mt-3">{tt("public_success_sub")}</p>
                        <div className="mt-8 inline-flex items-center gap-3 border border-gray-200 rounded-md px-4 py-3">
                            <span className="text-xs font-mono uppercase tracking-widest text-gray-500">{tt("public_ref_label")}</span>
                            <span className="font-mono font-bold text-[#00153f]">{success.ref}</span>
                        </div>
                    </motion.div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            <header style={{ background: brand, color: headerTextColor }}>
                <div className="max-w-3xl mx-auto px-6 py-8 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        {shop?.logo_url ? <img src={shop.logo_url} alt="" className="h-10 rounded-md p-1" style={{ background: `${headerTextColor}1a` }} /> : <div className="h-10 w-10 rounded-md flex items-center justify-center font-display font-bold" style={{ background: `${headerTextColor}26` }}>{shop?.name?.slice(0, 1)}</div>}
                        <div>
                            <div className="font-display font-bold text-lg leading-tight">{shop?.name}</div>
                            {shop?.url ? <a href={shop.url} target="_blank" rel="noreferrer" style={{ color: `${headerTextColor}b3` }} className="text-xs hover:opacity-100">{shop.url}</a> : null}
                        </div>
                    </div>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                data-testid="public-lang-toggle"
                                className="text-xs font-mono uppercase tracking-widest rounded-md px-2.5 py-1 flex items-center gap-1.5"
                                style={{ color: `${headerTextColor}cc`, borderColor: `${headerTextColor}33`, borderWidth: 1, borderStyle: "solid" }}
                                aria-label={tt("nav_lang_label")}
                            >
                                <Globe className="w-3.5 h-3.5" />
                                {lang.toUpperCase()}
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[160px]">
                            {[
                                { code: "de", label: "Deutsch" },
                                { code: "en", label: "English" },
                                { code: "fr", label: "Français" },
                                { code: "es", label: "Español" },
                            ].map((l) => (
                                <DropdownMenuItem
                                    key={l.code}
                                    data-testid={`public-lang-opt-${l.code}`}
                                    onSelect={() => setLang(l.code)}
                                    className="cursor-pointer flex items-center justify-between"
                                >
                                    <span className="flex items-center gap-2">
                                        <span className="text-xs font-mono text-gray-400 w-5">{l.code.toUpperCase()}</span>
                                        <span className="text-sm text-[#00153f]">{l.label}</span>
                                    </span>
                                    {lang === l.code && <Check className="w-3.5 h-3.5 text-[#0b905d]" />}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>

            <div className="max-w-3xl mx-auto px-6 py-10">
                <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-gray-500 mb-3">
                    <ShieldCheck className="w-3.5 h-3.5" /> EU 2023/2673
                </div>
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#00153f]">{tt("public_title")}</h1>
                <p className="text-gray-600 mt-2">{tt("public_sub")}</p>

                <form onSubmit={submit} className="mt-10 bg-white border border-gray-200 rounded-md p-6 sm:p-8 space-y-5" data-testid="public-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="sm:col-span-2">
                            <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{tt("public_email")} *</Label>
                            <Input data-testid="pf-email" required type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className="mt-2" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{tt("public_order_number")} *</Label>
                            <Input data-testid="pf-order-number" required value={form.order_number} onChange={(e) => setField("order_number", e.target.value)} className="mt-2" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{tt("public_full_name")}</Label>
                            <Input data-testid="pf-name" value={form.full_name} onChange={(e) => setField("full_name", e.target.value)} className="mt-2" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{tt("public_address")}</Label>
                            <Input data-testid="pf-address1" value={form.address_line1} onChange={(e) => setField("address_line1", e.target.value)} className="mt-2" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{tt("public_address2")}</Label>
                            <Input data-testid="pf-address2" value={form.address_line2} onChange={(e) => setField("address_line2", e.target.value)} className="mt-2" />
                        </div>
                        <div>
                            <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{tt("public_zip")}</Label>
                            <Input data-testid="pf-zip" value={form.postal_code} onChange={(e) => setField("postal_code", e.target.value)} className="mt-2" />
                        </div>
                        <div>
                            <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{tt("public_city")}</Label>
                            <Input data-testid="pf-city" value={form.city} onChange={(e) => setField("city", e.target.value)} className="mt-2" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{tt("public_country")}</Label>
                            <Input data-testid="pf-country" value={form.country} onChange={(e) => setField("country", e.target.value)} className="mt-2" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{tt("public_order_date")}</Label>
                            <Input data-testid="pf-order-date" type="date" value={form.order_date} onChange={(e) => setField("order_date", e.target.value)} className="mt-2" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{tt("public_products")}</Label>
                            <Textarea data-testid="pf-products" value={form.products} onChange={(e) => setField("products", e.target.value)} className="mt-2" rows={3} />
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{tt("public_reason")}</Label>
                            <Textarea data-testid="pf-reason" value={form.reason} onChange={(e) => setField("reason", e.target.value)} className="mt-2" rows={3} />
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{tt("public_iban")}</Label>
                            <Input data-testid="pf-iban" value={form.iban} onChange={(e) => setField("iban", e.target.value)} className="mt-2" />
                        </div>
                    </div>

                    {/* Attachments */}
                    {shop?.allow_attachments !== false && (
                        <div>
                            <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{tt("public_attachments")}</Label>
                            <label className="mt-2 border border-dashed border-gray-300 rounded-md p-5 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50" data-testid="pf-upload">
                                <Upload className="w-5 h-5 text-gray-400 mb-2" />
                                <span className="text-sm text-gray-600">{tt("public_file_hint")}</span>
                                <input type="file" multiple accept="image/*,.pdf" onChange={(e) => onFiles(e.target.files)} className="hidden" />
                            </label>
                            {files.length > 0 && (
                                <ul className="mt-3 space-y-2">
                                    {files.map((f, i) => (
                                        <li key={i} className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-2 text-sm">
                                            <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-gray-500" /> {f.filename}</div>
                                            <button type="button" onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    <p className="text-xs text-gray-500 leading-relaxed">{tt("public_legal")}</p>

                    <Button
                        type="submit"
                        disabled={submitting}
                        data-testid="pf-submit"
                        className="w-full h-12 hover:opacity-90 text-white"
                        style={{ background: brand }}
                    >
                        {submitting ? "..." : tt("public_submit")}
                    </Button>
                    <p className="text-xs text-gray-400">{tt("public_required_hint")}</p>
                </form>

                <footer className="mt-8 text-xs text-gray-500">
                    <div className="font-mono uppercase tracking-widest mb-1">{tt("public_recipient")}</div>
                    <div>{shop?.company_name || shop?.name}</div>
                    {shop?.address_line1 && <div>{shop.address_line1}{shop.address_line2 ? `, ${shop.address_line2}` : ""}</div>}
                    {(shop?.postal_code || shop?.city) && <div>{shop.postal_code} {shop.city}</div>}
                    {shop?.country && <div>{shop.country}</div>}
                    {shop?.contact_email && <div className="mt-1">{shop.contact_email}</div>}
                    <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-4">
                        <a href="/impressum" target="_blank" rel="noreferrer" className="hover:text-[#00153f]" data-testid="public-footer-impressum">{tt("footer_impressum")}</a>
                        <a href="/datenschutz" target="_blank" rel="noreferrer" className="hover:text-[#00153f]" data-testid="public-footer-datenschutz">{tt("footer_datenschutz")}</a>
                        <span className="ml-auto">{tt("public_powered_by")}</span>
                    </div>
                </footer>
            </div>
        </div>
    );
}
